import React, {useEffect, useMemo, useState} from 'react';

import {useGate, useStore} from 'effector-react';

import {useTranslation} from 'react-i18next';
import {generatePath, useHistory, useLocation, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';

import {ReactComponent as UploadIcon} from '../assets/svg/download.svg';
import {ReactComponent as HelperIcon} from '../assets/svg/helper.svg';

import {I18N_NS} from '../core/constants/names';
import {DOCUMENT_STATUS_COLORS} from '../core/constants/statuses';
import {DocumentProcessingStatus, DocumentStatus} from '../core/dictionaries';

import {CreateInvoiceGate, doCloseAllGates} from '../domain';
import {getAdapter} from '../init';
import {
    $allowedActionsForInvoice,
    $allowToCorrectInvoice,
    $blockNavigationFromCommon,
    $doesInvoiceExist,
    $fetchDataFxFailed,
    $invoiceIsRemoved,
    $invoiceState,
    $invoiceStatusInfo,
    $newInvoiceIsDone,
    setInvoiceState,
    setSellerTaxNumber,
} from '../models/common';
import {$blockNavigationFromCreate, $invoiceCreationPage, arrStores} from '../models/create';
import {$commonDataState, $everythingReady} from '../models/details';
import {$mainRole} from '../models/utils';
import {colors} from '../theme';

import {useClipboard} from '../utils/custom-hooks';

import {BackTo} from './common/BackTo';

import {ConfirmNavigationModal} from './ConfirmNavigationModal';
import {useNavigationBlocker} from './hooks/useNavigationBlocker';
import {InvoiceDocumentsTab} from './Pages/Documents';
import {InvoiceGeneralInfoTab} from './Pages/GeneralInfo';
import {InvoiceLinesTab} from './Pages/InvoiceLines';
import {TaxesAndTotalTab} from './Pages/TaxesTotal';
import {
    CloseAndLinkBack,
    CloseAndLinkBackIcon,
    CloseAndLinkBackText,
    HeadControl,
    InfoSection,
    InvoiceId,
    InvoiceIdContainer,
    InvoiceIdCopyButton,
    Label,
    ParentId,
    Root,
    Separator,
    Status,
    Wrapper,
} from './styled';
import {Button, ButtonVariant} from './ui-kit/Button';
import {HtmlText} from './ui-kit/HTMLText';
import {Icon} from './ui-kit/Icon';
import {Link} from './ui-kit/Link';
import {Spinner} from './ui-kit/Spinner';
import {Stepper} from './ui-kit/Stepper';
import {SpinnerLayer} from './ui-kit/Table2.0/Table/styled';
import {Tooltip} from './ui-kit/Tooltip';
import {ControlButtons} from './universalController/controlButtons';

const additionalStyles = `
    text-align: left;
    max-width: 250px;
    border-radius: 8px;
    opacity: 1 !important;
    `;

export const CreateInvoiceNew = () => {
    useGate(CreateInvoiceGate);
    const {id, invoiceId}: {id: string | undefined; invoiceId: string | undefined} = useParams();
    const location = useLocation();
    const role = useStore($mainRole);
    const invoiceIsRemoved = useStore($invoiceIsRemoved);
    const newInvoiceIsDone = useStore($newInvoiceIsDone);
    const fetchDataFxFailed = useStore($fetchDataFxFailed);
    const history = useHistory();
    const invoiceStatusInfo = useStore($invoiceStatusInfo);
    const currentPage = useStore($invoiceCreationPage);
    const arrStore = useStore(arrStores);
    const invoiceState = useStore($invoiceState);
    const everythingReady = useStore($everythingReady);
    const doesInvoiceExist = useStore($doesInvoiceExist);
    const allowedActionsForInvoice = useStore($allowedActionsForInvoice);
    const blockNavigationFromCommon = useStore($blockNavigationFromCommon);
    const blockNavigationFromCreate = useStore($blockNavigationFromCreate);
    const allowToCorrectInvoice = useStore($allowToCorrectInvoice);
    const {t} = useTranslation(I18N_NS);
    const shouldBlockNavigation = useMemo(
        () => blockNavigationFromCommon && blockNavigationFromCreate,
        [blockNavigationFromCommon, blockNavigationFromCreate],
    );

    const {showModal, handleConfirm, handleCancel} = useNavigationBlocker({
        when: shouldBlockNavigation,
    });

    useEffect(() => {
        const tin = new URLSearchParams(location.search).get('tin');
        if (tin) {
            setSellerTaxNumber(tin);
        }

        setInvoiceState(invoiceId ? 'new' : id ?? null);
    }, [invoiceId, id]);

    const getColors = (status: DocumentStatus | DocumentProcessingStatus) => ({
        color: DOCUMENT_STATUS_COLORS[status],
    });

    useEffect(() => {
        if (newInvoiceIsDone || invoiceIsRemoved || fetchDataFxFailed) {
            const setPath = generatePath(
                (location.state as {from: string | undefined})?.from ?? '/invoices/outgoing',
            );
            history.push(setPath);
        }
    }, [newInvoiceIsDone, invoiceIsRemoved, fetchDataFxFailed]);

    const GEN_INFO = 'pages.documents.invoices.create';

    function showStore() {
        console.log(arrStore);
        // handleUpdateInvoiceBody();
    }
    const copy = useClipboard();
    const copyCode = () => {
        if (!id) {
            return;
        }
        copy(id);
        toast.success(t('pages.documents.details.idCopied'));
    };

    const getStatus = useMemo(() => {
        if (!invoiceStatusInfo) {
            return null;
        }

        if (!invoiceStatusInfo.status) {
            return invoiceStatusInfo.processingStatus;
        }
        return invoiceStatusInfo.status;
    }, [invoiceStatusInfo]);

    const isTableSplitted = useMemo(() => role === 'DIRECTOR' || role === 'EMPLOYEE', [role]);

    const [downloadPending, setDownloadPending] = useState(false);

    const downloadPDF = async () => {
        if (!id) {
            return;
        }
        try {
            setDownloadPending(true);
            const response = await getAdapter().api.downloadInvoicePDF(id);
            const url = URL.createObjectURL(response);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${id}.pdf`;
            document.body.appendChild(a);
            a.click();
            URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (e) {
            console.error(e);
        } finally {
            setDownloadPending(false);
        }
    };

    return (
        <>
            <ConfirmNavigationModal
                show={showModal}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
            />
            {everythingReady ? (
                <>
                    <HeadControl>
                        <BackTo
                            to={(location.state as {from: string | undefined})?.from ?? '/invoices'}
                            isBack=""
                        >
                            {t('pages.documents.back')}
                        </BackTo>
                        <CloseAndLinkBack
                            to={(location.state as {from: string | undefined})?.from ?? '/invoices'}
                        >
                            <CloseAndLinkBackText>
                                {t(`${GEN_INFO}.buttons.close`)}
                            </CloseAndLinkBackText>
                            <CloseAndLinkBackIcon />
                        </CloseAndLinkBack>
                    </HeadControl>
                    <Root>
                        <InfoSection>
                            <Label>invoice</Label>
                            {doesInvoiceExist ? (
                                <InvoiceIdContainer>
                                    <InvoiceId>{invoiceState}</InvoiceId>
                                    <InvoiceIdCopyButton type="button" onClick={copyCode} />
                                </InvoiceIdContainer>
                            ) : (
                                <></>
                            )}
                            <Separator />
                            {getStatus && (
                                <Status color={getColors(getStatus).color}>
                                    {t(`dictionaries.documents.statusInvoice.${getStatus}`)}
                                </Status>
                            )}
                            {doesInvoiceExist && !allowToCorrectInvoice && (
                                <div>
                                    <Button
                                        type="button"
                                        style={{width: 'auto'}}
                                        variant={ButtonVariant.Outline}
                                        isLoading={downloadPending}
                                        size="sm"
                                        onClick={downloadPDF}
                                    >
                                        {t('pages.documents.download')}
                                        <Icon
                                            source={UploadIcon}
                                            style={{marginLeft: 10}}
                                            width={16}
                                            height={16}
                                        />
                                    </Button>
                                </div>
                            )}
                            {/* TODO: Uncomment this when the API is implemented */}
                            {allowedActionsForInvoice.correct && doesInvoiceExist ? (
                                <Wrapper>
                                    {allowToCorrectInvoice && (
                                        <>
                                            <div data-tip data-for="saveTooltip">
                                                <Icon
                                                    source={HelperIcon}
                                                    width={20}
                                                    height={20}
                                                    fillColor="transparent"
                                                    style={{color: colors.brandPrimary}}
                                                />
                                            </div>
                                            <Tooltip
                                                id="saveTooltip"
                                                place={'top'}
                                                backgroundColor="white"
                                                textColor="#111111"
                                                additionalStyles={
                                                    additionalStyles +
                                                    'box-shadow: 0px 0px 6px rgba(116, 116, 116, 0.3);'
                                                }
                                            >
                                                <HtmlText
                                                    Tag="span"
                                                    __html={t(
                                                        'pages.documents.invoices.tooltipSaveInfo',
                                                    )}
                                                />
                                            </Tooltip>
                                        </>
                                    )}
                                </Wrapper>
                            ) : (
                                <></>
                            )}
                            <ControlButtons id={id} />
                        </InfoSection>
                        {invoiceStatusInfo?.parentId && (
                            <ParentId>
                                {`Parent ID: `}
                                <Link
                                    onClick={() => {
                                        doCloseAllGates();
                                    }}
                                    to={{
                                        pathname: generatePath(
                                            getAdapter().ROUTES.INVOICE_DETAILS,
                                            {
                                                id: invoiceStatusInfo.parentId,
                                            },
                                        ),
                                        state: {
                                            from: location.pathname.startsWith('/invoices/')
                                                ? (location.state as {from: string | undefined})
                                                      ?.from
                                                : '/invoices',
                                        },
                                        ...(!isTableSplitted && {
                                            search: new URLSearchParams(location.search).get('tin')
                                                ? `?tin=${new URLSearchParams(location.search)
                                                      .get('tin')
                                                      ?.toString() ?? ''}`
                                                : '',
                                        }),
                                    }}
                                >
                                    {invoiceStatusInfo.parentId}
                                </Link>
                            </ParentId>
                        )}
                        <Stepper
                            extraStyles="margin-bottom: 20px;"
                            steps={[
                                {
                                    label: t(`${GEN_INFO}.steps.one`),
                                    component: <InvoiceGeneralInfoTab />,
                                    ...(!allowedActionsForInvoice.edit && {
                                        passed: true,
                                    }),
                                },
                                {
                                    label: t(`${GEN_INFO}.steps.two`),
                                    component: <InvoiceDocumentsTab />,
                                    ...(!allowedActionsForInvoice.edit && {
                                        passed: true,
                                    }),
                                },
                                {
                                    label: t(`${GEN_INFO}.steps.three`),
                                    component: <InvoiceLinesTab />,
                                    ...(!allowedActionsForInvoice.edit && {
                                        passed: true,
                                    }),
                                },
                                {
                                    label: t(`${GEN_INFO}.steps.four`),
                                    component: <TaxesAndTotalTab />,
                                    ...(!allowedActionsForInvoice.edit && {
                                        passed: true,
                                    }),
                                },
                            ]}
                            activeStep={currentPage}
                            isClickable={!allowedActionsForInvoice.edit}
                        />
                    </Root>
                </>
            ) : (
                <SpinnerLayer>
                    <Spinner />
                </SpinnerLayer>
            )}
        </>
    );
};
